import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, InstitutionPublicConfig, InstitutionDetectionInfo } from '../services/api';

// Default theme colors
const DEFAULT_THEME = {
  primary_color: '#07F77F', // Default green
  secondary_color: '#1aff8c' // Lighter green
};

interface InstitutionContextType {
  // Current institution data
  institution: InstitutionPublicConfig | null;
  detectionInfo: InstitutionDetectionInfo | null;
  
  // Loading states
  loading: boolean;
  error: string | null;
  
  // Theme and branding
  theme: {
    primary_color: string;
    secondary_color: string;
    logo_url?: string;
  };
  
  // Available institutions for development
  availableInstitutions: InstitutionPublicConfig[];
  
  // Methods
  refreshInstitution: () => Promise<void>;
  refreshAvailableInstitutions: () => Promise<void>;
  switchInstitution: (subdomain: string) => void; // For development
  applyTheme: () => void;
  
  // Helpers
  isInstitutionContext: boolean;
  institutionName: string;
  institutionSubdomain: string;
}

const InstitutionContext = createContext<InstitutionContextType | undefined>(undefined);

export const useInstitution = () => {
  const context = useContext(InstitutionContext);
  if (context === undefined) {
    throw new Error('useInstitution must be used within an InstitutionProvider');
  }
  return context;
};

export const InstitutionProvider = ({ children }: { children: ReactNode }) => {
  
  const [institution, setInstitution] = useState<InstitutionPublicConfig | null>(null);
  const [detectionInfo, setDetectionInfo] = useState<InstitutionDetectionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableInstitutions, setAvailableInstitutions] = useState<InstitutionPublicConfig[]>([]);
  
  // Theme state based on current institution
  const [theme, setTheme] = useState(DEFAULT_THEME);

  // Detect and load institution on mount
  useEffect(() => {
    let isMounted = true;
    
    const detectInstitution = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Debug logging for staging/development
        const hostname = window.location.hostname;
        const isStaging = hostname === 'staging.maxel.ai';
        if (isStaging || process.env.NODE_ENV === 'development') {
          console.log('[InstitutionContext] Detecting institution for:', {
            hostname,
            url: window.location.href,
            search: window.location.search,
            institutionParam: new URLSearchParams(window.location.search).get('institution')
          });
        }
        
        // Get current institution detection info
        // We need to pass the current URL parameters to the API call
        const detection = await api.institution.getCurrent();
        
        if (isStaging || process.env.NODE_ENV === 'development') {
          console.log('[InstitutionContext] Detection result:', detection);
        }
        
        if (isMounted) {
          setDetectionInfo(detection);
          
          if (detection.institution) {
            setInstitution(detection.institution);
            
            if (isStaging || process.env.NODE_ENV === 'development') {
              console.log('[InstitutionContext] Institution found:', detection.institution.name);
            }
            
            // Update theme
            const newTheme = {
              primary_color: detection.institution.primary_color || DEFAULT_THEME.primary_color,
              secondary_color: detection.institution.secondary_color || DEFAULT_THEME.secondary_color,
              logo_url: detection.institution.logo_url
            };
            setTheme(newTheme);
          } else {
            if (isStaging || process.env.NODE_ENV === 'development') {
              console.log('[InstitutionContext] No institution detected, using default theme');
            }
            setInstitution(null);
            setTheme(DEFAULT_THEME);
          }
        }
        
        // Load available institutions (for development switcher)
        try {
          const available = await api.institution.getAvailable();
          if (isMounted) {
            setAvailableInstitutions(available);
          }
        } catch (err) {
          console.warn('InstitutionProvider: Failed to load available institutions:', err);
        }
        
      } catch (err) {
        console.error('InstitutionProvider: Institution detection failed:', err);
        
        // Enhanced error logging for staging/development
        const hostname = window.location.hostname;
        const isStaging = hostname === 'staging.maxel.ai';
        if (isStaging || process.env.NODE_ENV === 'development') {
          console.error('[InstitutionContext] Detection error details:', {
            error: err,
            hostname,
            url: window.location.href,
            institutionParam: new URLSearchParams(window.location.search).get('institution'),
            apiBaseUrl: (() => {
              if (window.location.hostname === 'localhost') {
                return 'http://localhost:8001/api/v1';
              } else if (window.location.hostname === 'staging.maxel.ai') {
                return '/api';
              } else {
                return '/api/v1';
              }
            })()
          });
        }
        
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to detect institution');
          setInstitution(null);
          setDetectionInfo(null);
          setTheme(DEFAULT_THEME);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    detectInstitution();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Apply theme to CSS custom properties whenever theme changes
  useEffect(() => {
    applyThemeToDocument();
  }, [theme]);

  const applyThemeToDocument = () => {
    const root = document.documentElement;
    
    // Apply primary colors
    root.style.setProperty('--institution-primary-color', theme.primary_color);
    root.style.setProperty('--institution-secondary-color', theme.secondary_color);
    
    // Generate hover colors (slightly darker)
    const primaryHover = adjustColorBrightness(theme.primary_color, -10);
    const secondaryHover = adjustColorBrightness(theme.secondary_color, -10);
    root.style.setProperty('--institution-primary-hover', primaryHover);
    root.style.setProperty('--institution-secondary-hover', secondaryHover);
    
    // Update gradients
    root.style.setProperty('--institution-gradient', 
      `linear-gradient(135deg, ${theme.primary_color}, ${theme.secondary_color})`);
    root.style.setProperty('--institution-gradient-hover', 
      `linear-gradient(135deg, ${primaryHover}, ${secondaryHover})`);
  };
  
  // Helper function to adjust color brightness
  const adjustColorBrightness = (color: string, percent: number): string => {
    // Remove # if present
    const hex = color.replace('#', '');
    
    // Parse RGB values
    const num = parseInt(hex, 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    
    // Ensure values stay within 0-255 range
    const clamp = (val: number) => Math.max(0, Math.min(255, val));
    
    return `#${(0x1000000 + (clamp(R) * 0x10000) + (clamp(G) * 0x100) + clamp(B)).toString(16).slice(1)}`;
  };

  const refreshInstitution = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const detection = await api.institution.getCurrent();
      setDetectionInfo(detection);
      
      if (detection.institution) {
        setInstitution(detection.institution);
        const newTheme = {
          primary_color: detection.institution.primary_color || DEFAULT_THEME.primary_color,
          secondary_color: detection.institution.secondary_color || DEFAULT_THEME.secondary_color,
          logo_url: detection.institution.logo_url
        };
        setTheme(newTheme);
      } else {
        setInstitution(null);
        setTheme(DEFAULT_THEME);
      }
    } catch (err) {
      console.error('InstitutionProvider: Failed to refresh institution:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh institution');
    } finally {
      setLoading(false);
    }
  };

  const refreshAvailableInstitutions = async () => {
    try {
      const available = await api.institution.getAvailable();
      setAvailableInstitutions(available);
    } catch (err) {
      console.error('InstitutionProvider: Failed to refresh available institutions:', err);
    }
  };

  const switchInstitution = (subdomain: string) => {
    // Allow institution switching in development and staging environments
    const hostname = window.location.hostname;
    const isDevelopment = process.env.NODE_ENV === 'development';
    const isStaging = hostname === 'staging.maxel.ai' || hostname === 'localhost';
    
    if (isDevelopment || isStaging) {
      const url = new URL(window.location.href);
      if (subdomain === 'none') {
        url.searchParams.delete('institution');
      } else {
        url.searchParams.set('institution', subdomain);
      }
      window.location.href = url.toString();
    } else {
      console.warn('switchInstitution is only available in development and staging modes');
    }
  };

  const applyTheme = () => {
    applyThemeToDocument();
  };

  const value: InstitutionContextType = {
    institution,
    detectionInfo,
    loading,
    error,
    theme,
    availableInstitutions,
    refreshInstitution,
    refreshAvailableInstitutions,
    switchInstitution,
    applyTheme,
    
    // Computed properties
    isInstitutionContext: institution !== null,
    institutionName: institution?.name || 'Maxel',
    institutionSubdomain: institution?.subdomain || ''
  };

  return (
    <InstitutionContext.Provider value={value}>
      {children}
    </InstitutionContext.Provider>
  );
}; 